// 検索キーワード置換（hooks/search-word.ts）が使う型番リストの取得（Issue #62）。
//
// リストは public/w/typecodes.json（リポジトリにコミット済みの静的ファイル。約 68KB、gzip 約 18KB）で、
// ビルドで dist/w にコピーされ storefront.js と同じ場所（/w/typecodes.json）から配信される。storefront.js には埋め込まない
// （長音も全角ハイフンも含まない検索語では要らないため。検索語の約 44% がそれ）。
//
// 取得は 1 セッションに最大 1 回。sessionStorage に入れておき、同じタブ内の 2 回目以降は通信しない。
// sessionStorage が使えない（プライベートモード等）ときはページ内のメモリだけで持つ。
// 取れなかったときは null を返し、呼び出し側は置換しない側に倒す（検索は止めない）。
//
// ★ 再生成の手順は README「検索キーワードの置換」の節。本番 DB（読み取り専用接続）で
//   SELECT DISTINCT TypeCode FROM ProductItemInfos WHERE TypeCode LIKE N'%-%' ORDER BY TypeCode
//   を流して JSON 配列にし、public/w/typecodes.json を差し替えて PR を出す（田口さん判断: 型番はそこまで増えないので手動更新）。

export const TYPE_CODES_FILE = 'typecodes.json'
export const TYPE_CODES_STORAGE_KEY = 'wu.typecodes.v1'
const DEFAULT_URL = 'https://front.workup-system.com/w/' + TYPE_CODES_FILE

/** storefront.js を読んでいる <script src> と同じ場所から取る（ローカル検証で localhost:5984 を読ませたときも追従する） */
export function typeCodesUrl(doc: Document = document): string {
  const script = doc.querySelector<HTMLScriptElement>('script[src*="/w/storefront."]')
  const src = script?.getAttribute('src')
  if (!src) return DEFAULT_URL
  return src.replace(/\/w\/storefront\.[^/?#]*(?:[?#].*)?$/, '/w/' + TYPE_CODES_FILE)
}

export function parseTypeCodes(json: string | null): string[] | null {
  if (!json) return null
  try {
    const v: unknown = JSON.parse(json)
    return Array.isArray(v) && v.every((x) => typeof x === 'string') ? (v as string[]) : null
  } catch {
    return null
  }
}

function readCache(): string[] | null {
  try {
    return parseTypeCodes(sessionStorage.getItem(TYPE_CODES_STORAGE_KEY))
  } catch {
    return null
  }
}

function writeCache(json: string): void {
  try {
    sessionStorage.setItem(TYPE_CODES_STORAGE_KEY, json)
  } catch {
    /* 容量超過やプライベートモード。メモリ側だけで持つ */
  }
}

let inMemory: Promise<string[] | null> | null = null

/** 型番リスト。取れなければ null（置換しない側に倒す） */
export function loadTypeCodes(): Promise<string[] | null> {
  if (inMemory) return inMemory
  const cached = readCache()
  if (cached) {
    inMemory = Promise.resolve(cached)
    return inMemory
  }
  inMemory = fetch(typeCodesUrl(), { cache: 'default' })
    .then(async (r) => {
      if (!r.ok) return null
      const text = await r.text()
      const list = parseTypeCodes(text)
      if (list) writeCache(text)
      return list
    })
    .catch(() => null)
    .then((list) => {
      if (!list) inMemory = null // 失敗は覚えない（次の検索でもう一度だけ試す）
      return list
    })
  return inMemory
}
